import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Copy, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Renders any admin-built form definition and posts it to the single capture path. */
export const DiosaForm = ({ form, popupId, onSuccess, compact = false }) => {
  const [values, setValues] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  const set = (id, v) => setValues(p => ({ ...p, [id]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/forms/${form.slug}/submit${popupId ? `?popup_id=${popupId}` : ''}`,
        { values },
      );
      setDone(data);
      onSuccess?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'We could not save that — please try again');
    }
    setSending(false);
  };

  if (done) {
    return (
      <div className="text-center py-2" data-testid="form-success">
        <div className="w-10 h-10 rounded-full bg-[#EEDAC5] flex items-center justify-center mx-auto mb-4">
          <Check size={18} className="text-[#1B1815]" />
        </div>
        <p className="text-[15px] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="form-success-message">
          {done.message}
        </p>
        {done.coupon && (
          <div className="border border-[#B08D57] bg-[#FAF6EF] p-4 mt-4" data-testid="form-coupon">
            <p className="text-[9px] uppercase tracking-[0.2em] text-[#595956] mb-1">{done.coupon.label}</p>
            <div className="flex items-center justify-center gap-2">
              <span className="font-mono text-lg tracking-wider" data-testid="form-coupon-code">{done.coupon.code}</span>
              <button type="button" onClick={() => { navigator.clipboard?.writeText(done.coupon.code); toast.success('Code copied'); }}
                className="text-[#595956] hover:text-[#B08D57]" data-testid="form-coupon-copy">
                <Copy size={13} />
              </button>
            </div>
            <p className="text-[10px] text-[#595956] mt-2 leading-relaxed">
              {done.coupon.min_order_amount > 0 ? `On orders above ₹${done.coupon.min_order_amount}. ` : ''}
              Apply it yourself at checkout — we never add it for you.
            </p>
          </div>
        )}
        {done.download && (
          <a href={done.download.url} target="_blank" rel="noreferrer"
            className="btn-luxe-outline text-[10px] py-2 px-4 mt-4 inline-flex items-center gap-2" data-testid="form-download">
            <Download size={12} /> {done.download.label}
          </a>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3.5" data-testid="diosa-form">
      {!compact && form.title && (
        <h3 className="text-xl leading-snug" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="form-title">
          {form.title}
        </h3>
      )}
      {!compact && form.supporting_text && (
        <p className="text-[12px] text-[#595956] leading-relaxed" data-testid="form-supporting-text">{form.supporting_text}</p>
      )}

      {(form.fields || []).map(f => (
        <div key={f.id} data-testid={`form-field-${f.id}`}>
          {f.input !== 'checkbox' && (
            <label className="block text-[9px] uppercase tracking-[0.18em] text-[#595956] mb-1.5">
              {f.label}{f.required ? ' *' : ''}
            </label>
          )}
          {f.input === 'select' ? (
            <select value={values[f.id] || ''} onChange={e => set(f.id, e.target.value)} required={f.required}
              className="w-full text-sm py-2" data-testid={`form-input-${f.id}`}>
              <option value="">{f.placeholder || 'Select…'}</option>
              {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : f.input === 'textarea' ? (
            <textarea value={values[f.id] || ''} onChange={e => set(f.id, e.target.value)} required={f.required}
              placeholder={f.placeholder} rows={3} className="w-full text-sm py-2" data-testid={`form-input-${f.id}`} />
          ) : f.input === 'checkbox' ? (
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={!!values[f.id]} onChange={e => set(f.id, e.target.checked)}
                required={f.required} className="accent-[#B08D57] mt-0.5" data-testid={`form-input-${f.id}`} />
              <span className="text-[11px] text-[#595956] leading-relaxed">{f.label}{f.required ? ' *' : ''}</span>
            </label>
          ) : (
            <input type={f.input} value={values[f.id] || ''} onChange={e => set(f.id, e.target.value)}
              required={f.required} placeholder={f.placeholder} className="w-full text-sm py-2"
              data-testid={`form-input-${f.id}`} />
          )}
          {f.help_text && <p className="text-[10px] text-[#595956] mt-1 leading-relaxed">{f.help_text}</p>}
        </div>
      ))}

      <button type="submit" disabled={sending} className="btn-luxe text-[10px] py-2.5 px-6 w-full disabled:opacity-50" data-testid="form-submit">
        {sending ? 'Sending…' : (form.cta_text || 'Submit')}
      </button>
    </form>
  );
};

export default DiosaForm;
